import React from "react";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import SEO from "../components/common/SEO";
const SMALLSAT = [
    { t: "Payload (Mission Core)", b: "LiDAR / hyperspectral / optical sensors, RF payloads (communication, SAR). This is where our photonic sensing systems fit." },
    { t: "Structure & Thermal", b: "Mechanical frame, passive/active thermal control." },
    { t: "Power System (EPS)", b: "Solar panels, batteries, power distribution." },
    { t: "ADCS (Attitude Determination & Control)", b: "Star trackers, gyros, reaction wheels — precision pointing critical for LiDAR." },
    { t: "Onboard Computer (OBC)", b: "Data handling, command execution, AI/edge processing (emerging)." },
    { t: "Communication System", b: "Telemetry, Tracking & Command (TT&C), downlink." },
    { t: "Propulsion (optional)", b: "Orbit control, station keeping." },
];

export default function Platforms() {
    return (
        <>
            <SEO
                title="Platforms — SmallSat & Drone Architectures · Skovomax"
                description="SmallSat and drone platforms engineered around photonic payloads: LiDAR, hyperspectral and multispectral sensing for orbital and airborne surveys."
            />
            <section className="relative pt-28 pb-16 border-b border-white/10 overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-20 pointer-events-none" />
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <SectionLabel label="Platforms / 02" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Architectures
                            <br />
                            <span className="italic text-white/70">for sensing at altitude.</span>
                        </h1>
                    </Reveal>
                </div>
            </section>

            {/* SmallSat */}
            <section className="py-28 border-b border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-5">
                            <SectionLabel number="01" label="SmallSat Architecture" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight mb-6">
                                Orbital sensing platform.
                            </h2>
                            <p className="text-white/60 leading-relaxed mb-8">
                                A SmallSat is a systems-of-systems: payload, structure, power, attitude control,
                                compute, comms and propulsion — all sized and coupled to the mission. Our
                                photonic payloads drive the design of pointing, thermal, and downlink budgets.
                            </p>
                            <img
                                src="https://images.pexels.com/photos/30596892/pexels-photo-30596892.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Satellite over Earth"
                                className="w-full aspect-[4/3] object-cover frame-corners"
                            />
                        </div>
                        <div className="md:col-span-6 md:col-start-7">
                            <div className="tech-label mb-3">Core subsystems</div>
                            <div className="border border-white/10">
                                {SMALLSAT.map((s, i) => (
                                    <div key={s.t} className="flex items-start gap-6 p-6 border-b border-white/10 last:border-b-0">
                                        <div className="font-mono-tech text-[11px] tracking-[0.2em] text-[var(--accent-indigo)] w-10 pt-1">
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div>
                                            <div className="font-serif-display text-xl mb-1">{s.t}</div>
                                            <div className="text-white/55 text-sm leading-relaxed">{s.b}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 p-6 border border-[var(--accent-indigo)]/40 bg-[var(--accent-indigo)]/5">
                                <div className="tech-label mb-2">System Flow</div>
                                <div className="font-mono-tech text-sm text-white">
                                    Sensor → Data → Processing → Transmission → Ground Intelligence
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Drone */}
            <section className="py-28 bg-[#0a0a18]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-6">
                            <img
                                src="https://static.prod-images.emergentagent.com/jobs/bdce326b-0e13-497c-b849-fc45204e033b/images/6fbe45fcd0652e32cf837f3a1d88899ea4a26de167a1838c236fe53e51017f1f.png"
                                alt="Industrial drone scanning"
                                className="w-full aspect-[4/3] object-cover frame-corners"
                            />
                        </div>
                        <div className="md:col-span-6">
                            <SectionLabel number="02" label="Drone Architecture" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight mb-6">
                                Airborne photonic surveys.
                            </h2>
                            <p className="text-white/60 leading-relaxed mb-8">
                                Our drone platforms carry photonic LiDAR, multispectral and hyperspectral payloads
                                tuned for terrain reconstruction, vegetation–infrastructure analysis, and utility
                                corridor inspection at high spatial resolution.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { k: "Payload", v: "Photonic LiDAR · HSI" },
                                    { k: "Altitude", v: "50–250 m AGL" },
                                    { k: "Endurance", v: "35–60 min" },
                                    { k: "Output", v: "LOD2–LOD5 point clouds" },
                                ].map((m) => (
                                    <TechCard key={m.k} className="!p-5">
                                        <div className="font-mono-tech text-[11px] tracking-[0.2em] text-white/50 mb-1">
                                            {m.k}
                                        </div>
                                        <div className="font-serif-display text-2xl text-white">{m.v}</div>
                                    </TechCard>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
